import React, { useState } from 'react';
import { RiAddFill, RiDeleteBinLine } from 'react-icons/ri';
import SubmitButton from './SubmitButton';
import AddressFields from './AddressFields';
import {
  Button,
  Divider,
  Field,
  HelperText,
  IconButton,
  Label,
  Modal,
  TextInput,
} from './Ui';
import { formateZodError, LocationTypes } from '../../Utlils';
import { zodLocationSchema } from '../../pages/Validators';
import { useGlobalContext } from '../../Contexts/GlobalContext';
import MultiSelectDropdown from '../MultiSelectDropdown';


const AddLocationModal = ({
  showModal,
  setShowModal,
  handleCloseModal,
  modalData,
  setModalData,
  setCustomerData,
  edit = false,
  ediIndex,
  showInvoicePrefixInput = false,
  taxIdsRequired = false,
}) => {
  const { setSnackbar } = useGlobalContext();
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (name, value) => {
    setModalData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNestedChange = (
    nest,
    name,
    value,
  ) => {
    setModalData((prev) => ({
      ...prev,
      [nest]: { ...prev[nest], [name]: value },
    }));
    if (formErrors[nest] && formErrors[nest][name]) {
      setFormErrors((prev) => ({
        ...prev,
        [nest]: { ...prev[nest], [name]: undefined },
      }));
    }
  };

  const handleAddRow = () => {
    setModalData((prev) => ({
      ...prev,
      taxIds: [...(prev?.taxIds || []), { label: '', value: '' }],
    }));
  };

  const handleRemoveRow = (index) => {
    setModalData((prev) => ({
      ...prev,
      taxIds: prev?.taxIds?.filter((_, i) => i !== index),
    }));
  };

  const handleChangeRow = (index, name, value) => {
    setModalData((prev) => ({
      ...prev,
      taxIds: prev?.taxIds?.map((item, i) =>
        i === index ? { ...item, [name]: value } : item,
      ),
    }));
    if (formErrors?.companyCodes?.[index]?.[name]) {
      setFormErrors((prev) => ({
        ...prev,
        companyCodes: {
          ...prev?.companyCodes,
          [index]: { ...prev?.companyCodes?.[index], [name]: undefined },
        },
      }));
    }
  };

  const handleFormSubmit = (e) => {
    try {
      e.preventDefault();
      const validationResult = zodLocationSchema.safeParse(modalData);
      if (validationResult.success) {
        if (!edit) {
          setCustomerData((prev) => ({
            ...prev,
            locations: [...(prev?.locations || []), validationResult.data],
          }));
        } else {
          setCustomerData((prev) => ({
            ...prev,
            locations: prev?.locations?.map((item, i) =>
              i === ediIndex ? validationResult.data : item,
            ),
          }));
        }
        handleCloseModal();
      } else {
        setFormErrors(formateZodError(validationResult.error.errors));
      }
    } catch (error) {
      console.log('error', error);
    //   showNotice(setNotice, 'Something went wrong', 'Unable to update address', 'error');
    }
  };
  

  return (
    <Modal
      showModal={showModal}
      setShowModal={() => {
        handleCloseModal();
        setShowModal(!showModal);
      }}
      closeOnOutsideClick={false}
    >
      <form id="innerForm" className="space-y-[16px]" onSubmit={handleFormSubmit}>
        <h1 className="text-[18px] font-[600]">
          {edit ? 'Edit Location' : 'Add Location'}
        </h1>
        <Divider />

        <div className="containerScroll max-h-[450px] space-y-4 overflow-y-auto pr-2">
          <AddressFields
            addressKey="address"
            modalData={modalData}
            formErrors={formErrors}
            onNestedChange={handleNestedChange}
          />

          {/* address type */}
          <Field label="Address Type" required error={formErrors?.locationType}>
            <MultiSelectDropdown
              search
              options={LocationTypes?.map((d) => ({
                label: d.charAt(0).toUpperCase() + d.slice(1),
                value: d,
              }))}
              setSelected={(e) => {
                const value = e ;
                handleChange('locationType', value?.[0].value);
              }}
              selected={[
                {
                  label: modalData?.locationType
                    ? modalData.locationType.charAt(0).toUpperCase() +
                      modalData.locationType.slice(1)
                    : '',
                  value: modalData?.locationType || '',
                },
              ]}
            />
          </Field>

          {/* business IDs */}
          <div className="space-y-[6px]">
            <div className="containerScroll max-h-[200px] space-y-[6px] overflow-y-auto pr-2">
              <Label required={taxIdsRequired}>Business IDs</Label>

              {modalData?.taxIds?.map((item, index, arr) => (
                <div className="space-y-2" key={index}>
                  <div className="flex w-full items-start gap-[10px]">
                    <div className="w-1/2">
                      <TextInput
                        placeholder="ID Name"
                        value={item?.label || ''}
                        required
                        inputProps={{ minLength: 3, maxLength: 24 }}
                        error={formErrors?.companyCodes?.[index]?.label}
                        onChange={(e) =>
                          handleChangeRow(index, 'label', e.target.value)
                        }
                      />
                      {!!formErrors?.companyCodes?.[index]?.label && (
                        <HelperText>{formErrors.companyCodes[index].label}</HelperText>
                      )}
                    </div>
                    <div className="w-1/2">
                      <TextInput
                        placeholder="ID Value"
                        value={item?.value || ''}
                        required
                        inputProps={{ minLength: 3, maxLength: 50 }}
                        error={formErrors?.companyCodes?.[index]?.value}
                        onChange={(e) =>
                          handleChangeRow(index, 'value', e.target.value)
                        }
                      />
                      {!!formErrors?.companyCodes?.[index]?.value && (
                        <HelperText>{formErrors.companyCodes[index].value}</HelperText>
                      )}
                    </div>
                    {(!taxIdsRequired || arr.length > 1) && (
                      <IconButton
                        aria-label="Remove ID"
                        variant="soft"
                        intent="error"
                        onClick={() => handleRemoveRow(index)}
                      >
                        <RiDeleteBinLine size={16} />
                      </IconButton>
                    )}
                  </div>

                  {!!Object.values(formErrors?.taxIds?.[index] || {}).length && (
                    <HelperText>
                      {Object.values(formErrors?.taxIds?.[index] || {}).map(
                        (d, i) => (
                          <div key={i}>{d}</div>
                        ),
                      )}
                    </HelperText>
                  )}
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="soft"
              onClick={handleAddRow}
              startIcon={<RiAddFill size={20} />}
            >
              Add More IDs
            </Button>
          </div>

          {/* invoice prefix */}
          {showInvoicePrefixInput && (
            <Field label="Invoice Prefix">
              <TextInput
                name="invoicePrefix"
                placeholder="Enter Invoice Prefix"
                value={modalData?.invoicePrefix || ''}
                error={formErrors?.invoicePrefix}
                onChange={(e) => handleChange('invoicePrefix', e.target.value)}
              />
            </Field>
          )}
        </div>

        {/* buttons */}
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="outlined"
            intent="default"
            type="button"
            onClick={handleCloseModal}
          >
            Close
          </Button>
          <SubmitButton size="sm" name="Save" />
        </div>
      </form>
    </Modal>
  );
};

export default AddLocationModal;